import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

interface TenantFeature {
  id: string;
  tenant_id: string;
  // 基本機能
  customers_enabled: boolean;
  products_enabled: boolean;
  documents_enabled: boolean;
  // コミュニケーション機能
  chat_enabled: boolean;
  staff_chat_enabled: boolean;
  // AI機能
  deep_research_enabled: boolean;
  legal_check_enabled: boolean;
  marketing_ai_enabled: boolean;
  workflow_ai_enabled: boolean;
  knowledge_base_enabled: boolean;
  // 管理機能
  staff_management_enabled: boolean;
  notifications_enabled: boolean;
  invitations_enabled: boolean;
  session_management_enabled: boolean;
  // メタデータ
  notes?: string;
  updated_at?: string;
  updated_by?: string;
}

interface FeaturePreset {
  name: string;
  description: string;
  [key: string]: any;
}

const featureCategories = [
  {
    title: '基本機能',
    features: [
      { key: 'customers_enabled', label: '顧客管理', description: '顧客情報の管理・編集' },
      { key: 'products_enabled', label: '商品管理', description: '商品・サービス情報の管理' },
      { key: 'documents_enabled', label: 'AIドキュメント管理', description: 'ファイルアップロード・管理' },
    ]
  },
  {
    title: 'コミュニケーション',
    features: [
      { key: 'chat_enabled', label: 'チャット', description: '顧客とのチャット機能' },
      { key: 'staff_chat_enabled', label: 'スタッフチャット', description: '内部コミュニケーション' },
    ]
  },
  {
    title: 'AI機能',
    features: [
      { key: 'deep_research_enabled', label: 'Deep Research', description: '顧客・競合分析' },
      { key: 'legal_check_enabled', label: 'AI法務チェック', description: '契約書の自動チェック' },
      { key: 'marketing_ai_enabled', label: 'マーケティングAI', description: 'マーケティング支援' },
      { key: 'workflow_ai_enabled', label: 'ワークフローAI', description: '業務フロー最適化' },
      { key: 'knowledge_base_enabled', label: 'ナレッジベース', description: 'AI知識検索・法務文書管理' },
    ]
  },
  {
    title: '管理機能',
    features: [
      { key: 'staff_management_enabled', label: 'スタッフ管理', description: 'スタッフアカウント管理' },
      { key: 'notifications_enabled', label: '通知管理', description: 'システム通知の管理' },
      { key: 'invitations_enabled', label: '招待管理', description: 'ユーザー招待機能' },
      { key: 'session_management_enabled', label: 'セッション管理', description: 'ログインセッション管理' },
    ]
  }
];

function TenantFeatures() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [features, setFeatures] = useState<TenantFeature | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState<Record<string, FeaturePreset>>({});
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [tenantName, setTenantName] = useState<string>('');

  useEffect(() => {
    loadTenantFeatures();
    loadPresets();
  }, [tenantId]);

  const loadTenantFeatures = async () => {
    try {
      setLoading(true);
      
      // テナント情報を取得
      try {
        const tenants = await apiService.getAllTenants();
        const currentTenant = tenants.find((t: any) => t.id === tenantId);
        if (currentTenant) {
          setTenantName(currentTenant.name);
        }
      } catch (error) {
        console.warn('Failed to load tenant info:', error);
        setTenantName(`テナント${tenantId}`);
      }
      
      // 機能設定を取得
      const data = await apiService.getTenantFeatures(tenantId!);
      setFeatures(data);
    } catch (error) {
      console.error('Failed to load tenant features:', error);
      setMessage({ type: 'error', text: `機能設定の読み込みに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  const loadPresets = async () => {
    try {
      const data = await apiService.getFeaturePresets();
      setPresets(data);
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const handleFeatureChange = (featureKey: string, enabled: boolean) => {
    if (!features) return;
    
    setFeatures({
      ...features,
      [featureKey]: enabled
    });
  };

  const handleSave = async () => {
    if (!features) return;
    
    try {
      setSaving(true);
      const updatedFeatures = await apiService.updateTenantFeatures(tenantId!, features);
      setFeatures(updatedFeatures);
      setMessage({ type: 'success', text: '設定を保存しました' });
    } catch (error) {
      console.error('Failed to save features:', error);
      setMessage({ type: 'error', text: `設定の保存に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}` });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async (presetName: string) => {
    if (!features || !presets[presetName]) return;
    
    try {
      setSaving(true);
      const result = await apiService.applyFeaturePreset(tenantId!, presetName);
      setFeatures(result.features);
      setMessage({ type: 'success', text: result.message });
      setPresetDialogOpen(false);
    } catch (error) {
      console.error('Failed to apply preset:', error);
      setMessage({ type: 'error', text: `プリセットの適用に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}` });
    } finally {
      setSaving(false);
    }
  };

  const getEnabledFeaturesCount = () => {
    if (!features) return 0;
    return featureCategories.reduce((count, category) => {
      return count + category.features.filter(feature => 
        (features as any)[feature.key]
      ).length;
    }, 0);
  };

  const getTotalFeaturesCount = () => {
    return featureCategories.reduce((count, category) => count + category.features.length, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!features) {
    return (
      <Box>
        <Alert severity="error">機能設定を読み込めませんでした</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/tenants/${tenantId}`)}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">
            機能管理
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {tenantName} の機能設定
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setPresetDialogOpen(true)}
          >
            プリセット
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTenantFeatures}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* サマリーカード */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            設定サマリー
          </Typography>
          <Chip 
            label={`テナントID: ${tenantId}`} 
            variant="outlined" 
            size="small" 
          />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              有効な機能数
            </Typography>
            <Typography variant="h4" color="primary">
              {getEnabledFeaturesCount()} / {getTotalFeaturesCount()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              最終更新
            </Typography>
            <Typography variant="body1">
              {features.updated_at ? new Date(features.updated_at).toLocaleString('ja-JP') : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              メモ
            </Typography>
            <Typography variant="body1">
              {features.notes || 'なし'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 機能設定 */}
      <Grid container spacing={3}>
        {featureCategories.map((category, categoryIndex) => (
          <Grid item xs={12} md={6} key={categoryIndex}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {category.title}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {category.features.map((feature, featureIndex) => (
                <Box key={feature.key} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={(features as any)[feature.key]}
                        onChange={(e) => handleFeatureChange(feature.key, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {feature.label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* プリセット選択ダイアログ */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>プリセット選択</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.entries(presets).map(([key, preset]) => (
              <Grid item xs={12} md={4} key={key}>
                <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => applyPreset(key)}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {preset.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {preset.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(preset).filter(([k, v]) => k.endsWith('_enabled') && v).map(([k]) => (
                        <Chip
                          key={k}
                          label={k.replace('_enabled', '').replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" color="primary">
                      適用
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantFeatures;